import { useMutation } from '@tanstack/react-query';

import { useEnsureCsrfToken } from '~/context/CsrfContext';
import type { ToastData } from '~/hooks/useToast';

export type ReportCommentResponse = { toast: ToastData };

const isReportCommentResponse = (data: unknown): data is ReportCommentResponse => {
  if (typeof data !== 'object' || data === null) return false;
  return 'toast' in data;
};

const useReportCommentMutation = () => {
  const ensureCsrfToken = useEnsureCsrfToken();
  return useMutation({
    mutationFn: async (payload: { comment_id: string; reason: string }): Promise<ReportCommentResponse> => {
      const csrfToken = await ensureCsrfToken();
      const params = new URLSearchParams({ comment_id: payload.comment_id, reason: payload.reason });

      const response = await fetch('/report_community_comment', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          'X-CSRF-Token': csrfToken,
        },
        body: params,
      });

      const data: unknown = await response.json();

      if (!response.ok) {
        const message = isReportCommentResponse(data) ? data.toast.message : 'Failed to report comment';
        throw new Error(message);
      }

      if (!isReportCommentResponse(data)) throw new Error('report_comment_invalid_response');

      return data;
    },
  });
};

export default useReportCommentMutation;
